import React, { useState, useEffect, useRef } from 'react';
import { MapPinned } from 'lucide-react';
import { useMap } from 'react-leaflet/hooks';
import { createPortal } from 'react-dom';
import './MapLayer.css';

const MapLayer = ({ onLayerChange, popupOpen, onMenuStateChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState('carto');
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.map-layer-menu') && 
          !event.target.closest('.map-layer-button')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (popupOpen) {
      setShowMenu(false);
    }
  }, [popupOpen]);

  useEffect(() => {
    const isAnyMenuOpen = showMenu;
    if (onMenuStateChange) {
      onMenuStateChange(isAnyMenuOpen);
    }
  }, [showMenu, onMenuStateChange]);

  const map = useMap();

  const handleMouseEnter = () => {
    map.doubleClickZoom.disable();
  };

  const handleMouseLeave = () => {
    map.doubleClickZoom.enable();
  };

  const mapLayers = [
    { 
      id: 'cyclosm', 
      name: 'Bicycle', 
      url: 'https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
      attribution: '<a href="https://github.com/cyclosm/cyclosm-cartocss-style/releases" target="_blank">CyclOSM</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
    },
    { 
      id: 'usgs-satellite', 
      name: 'Satellite', 
      url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles courtesy of the <a href="https://usgs.gov/" target="_blank">U.S. Geological Survey</a>'
    },
    { 
      id: 'usgs-topo', 
      name: 'Topography', 
      url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles courtesy of the <a href="https://usgs.gov/" target="_blank">U.S. Geological Survey</a>'
    },
    { 
      id: 'osm', 
      name: 'Street (OSM)', 
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
    },
    { 
      id: 'carto', 
      name: 'Street (Default)', 
      url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
    }
  ];

  const MenuComponent = () => {
    if (!showMenu) return null;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    
    return (
      <div
        ref={menuRef}
        className="map-layer-menu"
        style={{
          left: `${buttonRect.right + 2}px`,
          bottom: `${window.innerHeight - buttonRect.bottom}px`,
          position: 'fixed'
        }}
      >
        {mapLayers.map((layer) => (
          <button
            key={layer.id}
            className={`map-layer-item ${layer.id === selectedLayer ? 'selected' : ''}`}
            onClick={() => {
              if (layer.id !== selectedLayer) {
                onLayerChange({ url: layer.url, attribution: layer.attribution });
                setSelectedLayer(layer.id);
                setShowMenu(false);
              }
            }}
          >
            {layer.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="leaflet-bottom leaflet-left map-layer">
      <div 
        className="leaflet-control"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button 
          ref={buttonRef}
          className={`map-layer-button ${showMenu ? 'active' : ''}`}
          onClick={() => setShowMenu(!showMenu)}
        >
          <MapPinned size={20} className="map-layer-icon" />
        </button>
        
        {showMenu && createPortal(
          <MenuComponent />,
          document.body
        )}
      </div>
    </div>
  );
};

export default MapLayer;